import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import BookingDialog from "./BookingDialog";
import ContactDialog from "./ContactDialog";
import { BALLOONS } from "../../lib/media";
import { useLang } from "../../lib/i18n";

function Balloon({
  src,
  alt,
  top,
  left,
  right,
  bottom,
  size = 200,
  rotate = 0,
  // y-offset (in px) the balloon starts BELOW its final position when the
  // section first enters view, then rises to 0 at section center, then
  // continues drifting beyond by `extraRise` px past the centre.
  riseFrom = 240,
  extraRise = -60,
  // small lateral drift across the whole scroll
  xDrift = 0,
  // gentle rotation drift across the scroll
  rotDrift = 0,
  drift = { y: 18, x: 8, r: 2, dur: 10 },
  delay = 0,
  layer = 20,
  scrollYProgress,
}) {
  // Scroll-driven y: starts +riseFrom (lower), passes through 0 at center,
  // then continues to extraRise (negative = continues upward) by the time
  // the section has fully scrolled past.
  const yScroll = useTransform(scrollYProgress, [0, 0.5, 1], [riseFrom, 0, extraRise]);
  const xScroll = useTransform(scrollYProgress, [0, 1], [0, xDrift]);
  const rScroll = useTransform(scrollYProgress, [0, 1], [0, rotDrift]);
  return (
    <motion.div
      style={{ y: yScroll, x: xScroll, rotate: rScroll, top, left, right, bottom, width: size, height: size, zIndex: layer }}
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: false, amount: 0.05 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className="absolute pointer-events-none"
    >
      <motion.div
        animate={{
          y: [0, -drift.y, 0, drift.y * 0.6, 0],
          x: [0, drift.x, 0, -drift.x * 0.7, 0],
          rotate: [rotate - drift.r, rotate + drift.r, rotate - drift.r * 0.5, rotate + drift.r * 0.8, rotate - drift.r],
        }}
        transition={{ duration: drift.dur, repeat: Infinity, ease: "easeInOut", delay }}
        className="w-full h-full"
      >
        <img src={src} alt={alt} className="w-full h-full object-contain balloon select-none" draggable={false} />
      </motion.div>
    </motion.div>
  );
}

function Butterfly({ src, size = 90, duration = 32, delay = 0, path, flipped = false }) {
  const xVals = path.map((p) => p.x);
  const yVals = path.map((p) => p.y);
  const rotVals = path.map((p) => p.r ?? 0);
  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ left: xVals, top: yVals, rotate: rotVals }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
        times: xVals.map((_, i) => i / (xVals.length - 1)),
      }}
      style={{ width: size, height: size, zIndex: 25, opacity: 1 }}
      className="absolute pointer-events-none"
    >
      <motion.div
        animate={{ scaleX: [1, 0.88, 1, 0.92, 1], scaleY: [1, 1.02, 1, 1.03, 1] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
        className="w-full h-full"
        style={{ transform: flipped ? "scaleX(-1)" : undefined }}
      >
        <img src={src} alt="Butterfly" className="w-full h-full object-contain balloon select-none" draggable={false} />
      </motion.div>
    </motion.div>
  );
}

export default function FinalCTA() {
  const { t } = useLang();
  const ref = useRef(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  // Scroll progress over the section: 0 when section enters viewport bottom, 1 when it leaves top
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Heart parallax: large heart fully visible behind the headline, perfectly
  // centred. Rises into place from below as the section enters view, then drifts
  // gently. Opacity capped so the headline reads cleanly through it.
  const heartY = useTransform(scrollYProgress, [0, 0.5, 1], ["60vh", "0vh", "-10vh"]);
  const heartScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 1.04]);
  const heartOpacity = useTransform(scrollYProgress, [0, 0.15, 0.5, 0.95, 1], [0, 0.45, 0.6, 0.6, 0.45]);

  // Subtle parallax for headline + buttons
  const yHead = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yBtns = useTransform(scrollYProgress, [0, 1], [25, -25]);

  // Butterfly paths (mirrored from hero, slightly different routes for variety)
  const pathA = [
    { x: "-6%", y: "65%", r: 8 },
    { x: "16%", y: "48%", r: -4 },
    { x: "38%", y: "30%", r: 8 },
    { x: "58%", y: "40%", r: -8 },
    { x: "78%", y: "26%", r: 6 },
    { x: "100%", y: "52%", r: -4 },
    { x: "104%", y: "44%", r: 8 },
    { x: "70%", y: "78%", r: -6 },
    { x: "30%", y: "82%", r: 4 },
    { x: "-6%", y: "65%", r: 8 },
  ];
  const pathB = [
    { x: "104%", y: "22%", r: -8 },
    { x: "82%", y: "38%", r: 6 },
    { x: "58%", y: "30%", r: -4 },
    { x: "32%", y: "44%", r: 10 },
    { x: "12%", y: "62%", r: -6 },
    { x: "-8%", y: "56%", r: 4 },
    { x: "20%", y: "20%", r: -10 },
    { x: "58%", y: "14%", r: 6 },
    { x: "92%", y: "26%", r: -4 },
    { x: "104%", y: "22%", r: -8 },
  ];

  return (
    <>
      <section
        ref={ref}
        id="book"
        data-testid="final-cta-section"
        style={{ position: "relative" }}
        className="relative w-full bg-white py-32 sm:py-48 text-center overflow-hidden min-h-[100svh] flex items-center justify-center"
      >
        {/* Decor balloons — mirrors the hero composition */}
        <Balloon
          src={BALLOONS.sunflower}
          alt="Inflatable sunflower"
          bottom="6%"
          right="4%"
          size={270}
          rotate={-4}
          scrollSpeed={-80}
          drift={{ y: 22, x: -14, r: 3, dur: 14 }}
          delay={0.5}
          layer={10}
          scrollYProgress={scrollYProgress}
        />
        <Balloon
          src={BALLOONS.camera}
          alt="Inflatable camera"
          top="18%"
          left="3%"
          size={310}
          rotate={-5}
          scrollSpeed={-110}
          drift={{ y: 22, x: 14, r: 3, dur: 11 }}
          delay={0.15}
          layer={20}
          scrollYProgress={scrollYProgress}
        />
        <Balloon
          src={BALLOONS.teddy}
          alt="Inflatable graduation teddy bear"
          bottom="4%"
          left="3%"
          size={330}
          rotate={3}
          scrollSpeed={-70}
          drift={{ y: 18, x: 10, r: 2.2, dur: 13 }}
          delay={0.4}
          layer={20}
          scrollYProgress={scrollYProgress}
        />
        <Balloon
          src={BALLOONS.books}
          alt="Inflatable stack of books"
          top="16%"
          right="3%"
          size={295}
          rotate={4}
          scrollSpeed={-130}
          drift={{ y: 22, x: -12, r: 2.8, dur: 12 }}
          delay={0.3}
          layer={20}
          scrollYProgress={scrollYProgress}
        />

        {/* Love Exclusive heart — large, perfectly centred behind the headline.
            Acts as a background framing device with the headline sitting inside. */}
        <motion.img
          src={BALLOONS.loveHeart}
          alt=""
          aria-hidden
          data-testid="final-cta-love-heart"
          className="absolute pointer-events-none balloon select-none"
          style={{
            left: "50%",
            top: "50%",
            width: "min(86vw, 1100px)",
            height: "min(86vw, 1100px)",
            marginLeft: "max(-43vw, -550px)",
            marginTop: "max(-43vw, -550px)",
            y: heartY,
            scale: heartScale,
            opacity: heartOpacity,
            zIndex: 5,
          }}
          draggable={false}
        />

        {/* Content */}
        <div className="relative z-30 max-w-[1400px] mx-auto px-6 sm:px-10">
          <motion.h2
            style={{ y: yHead }}
            data-testid="final-cta-headline"
            dir="auto"
            className="font-display font-black uppercase leading-[0.86] tracking-tighter text-black text-[14vw] sm:text-[10vw] lg:text-[8vw] mx-auto max-w-5xl"
          >
            {t("final.line1")}
            <br />
            {t("final.line2")}
          </motion.h2>

          <motion.div
            style={{ y: yBtns }}
            className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={() => setBookOpen(true)}
              data-testid="final-cta-book-btn"
              dir="auto"
              className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border border-black hover:bg-white hover:text-black transition-colors"
            >
              {t("final.book")}
            </button>
            <button
              onClick={() => setContactOpen(true)}
              data-testid="final-cta-contact-btn"
              dir="auto"
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-[11px] uppercase tracking-[0.25em] font-bold border border-black hover:bg-black hover:text-white transition-colors"
            >
              {t("final.contact")}
            </button>
          </motion.div>
        </div>

        {/* Butterflies — front layer */}
        <Butterfly src={BALLOONS.butterflyPink} size={88} duration={38} delay={0.6} path={pathA} />
        <Butterfly src={BALLOONS.butterflyBlue} size={78} duration={42} delay={2.2} path={pathB} flipped />

        {/* Copyright sits cleanly at the bottom of the section */}
        <div
          data-testid="final-cta-copyright"
          className="absolute inset-x-0 bottom-0 z-30 pointer-events-none px-6 sm:px-10 pb-6 sm:pb-8 flex items-center justify-center"
        >
          <span
            data-testid="footer-copyright"
            dir="auto"
            className="text-center font-body text-[10px] uppercase tracking-[0.28em] text-black/60"
          >
            {t("footer.copyright")}
          </span>
        </div>
      </section>

      <BookingDialog open={bookOpen} onOpenChange={setBookOpen} mode="book" />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </>
  );
}
